import React, { useContext } from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm } from 'react-hook-form';
import { DocumentNode, InternalRefetchQueryDescriptor, useMutation } from '@apollo/client';
import { Divider, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { CoherencePanelStyles, dividerStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import EntityTerminationTaskForm from './EntityTerminationTaskEditForm';
import { IEntityTerminationTask } from '../types';
import EntityTerminationTaskHistory from './EntityTerminationTaskHistory';
import DocumentsForm from '../../common/documents/DocumentsForm';
import UserContext from '../../../utils/authorization/UserContext';
import { StatUser } from '../../../utils/statApi/UsersApi';
import useDocumentsList from '../../../utils/hooks/useDocumentsList';
import {
  buildOwnerInput,
  buildAssigneesInput,
} from '../../../utils/statApi/entityTerminationTaskUtils';

interface IEntityTerminationTaskEditPanelProps {
  closePanel: () => void;
  isNewTask?: boolean;
  selectedtask?: IEntityTerminationTask;
  refetchQueries?: InternalRefetchQueryDescriptor[];
  createTaskMutation?: DocumentNode;
  updateTaskMutation: DocumentNode;
  companyCode?: string;
  actionItemId: number;
  actionItemStartDate?: Date | string | null;
  taskDtoVariableName?: string;
  actionItemIdFieldName?: string;
  isDescriptionReadOnly?: boolean;
  /** When true, shows a document upload section and sends document changes with the task save. */
  enableDocuments?: boolean;
}

const EntityTerminationTaskEditPanel = (
  props: IEntityTerminationTaskEditPanelProps,
): JSX.Element => {
  const {
    closePanel,
    isNewTask,
    selectedtask,
    refetchQueries,
    companyCode,
    actionItemId,
    createTaskMutation,
    updateTaskMutation,
    actionItemStartDate,
    taskDtoVariableName = 'legalInProcessActionItemTaskDto',
    actionItemIdFieldName = 'legalInProcessActionItemId',
    isDescriptionReadOnly = false,
    enableDocuments = false,
  } = props;
  const entityTerminationTaskForm = useForm();
  const { handleSubmit } = entityTerminationTaskForm;
  const styles = dividerStyles();
  const {
    currentDocuments,
    newDocuments,
    documentIdsToRemove,
    uploadIsInProgress,
    attachDocuments,
    deleteDocument,
    setUploadInProgress,
  } = useDocumentsList(selectedtask?.documents);
  const currentUser = useContext(UserContext) as StatUser | null;
  const isOwner = !!currentUser && selectedtask?.owner?.graphId === currentUser.graphGUID;
  const isAssignee =
    selectedtask?.assignees?.some((a) => a.graphId === currentUser?.graphGUID) ?? false;

  // TODO: Later update this to either show 'Edit' or 'Create New' based on props
  const title = isNewTask ? 'Create New ' : 'Edit ';

  const [createActionItemTask, { loading: createLoading, error: createError }] = useMutation(
    createTaskMutation ?? updateTaskMutation,
    {
      refetchQueries,
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const [updateActionItemTask, { loading: updateLoading, error: updateError }] = useMutation(
    updateTaskMutation,
    {
      refetchQueries,
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSaveClick = (formData: IEntityTerminationTask): void => {
    const ownerInput = buildOwnerInput(formData);
    const assigneesInput = buildAssigneesInput(formData);

    const actionItemTaskVariables = {
      [taskDtoVariableName]: {
        id: selectedtask?.id ?? null,
        [actionItemIdFieldName]: actionItemId,
        description: isDescriptionReadOnly ? selectedtask?.description ?? '' : formData.description,
        owner: ownerInput,
        comment: formData.comment,
        assignees: assigneesInput,
        response: formData.response,
        followUpFlagDate: formData.followUpFlagDate,
        completedDate: formData.completedDate,
        ...(enableDocuments && {
          documentsToAdd: newDocuments,
          documentIdsToRemove,
        }),
      },
      companyCode,
    };

    if (isNewTask && createTaskMutation) {
      createActionItemTask({ variables: { ...actionItemTaskVariables } });
    } else {
      updateActionItemTask({ variables: { ...actionItemTaskVariables } });
    }
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText={`${title} Task`}
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          mutationLoading={createLoading || updateLoading || uploadIsInProgress}
          saveLabel={isNewTask ? 'Add Task' : 'Save Task'}
          saveTitle={isNewTask ? 'Add Task' : 'Save Task'}
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
        />
      )}
    >
      <form onSubmit={handleSubmit(handleSaveClick)}>
        <EntityTerminationTaskForm
          form={entityTerminationTaskForm}
          selectedTask={selectedtask}
          isOwner={isOwner}
          isAssignee={isAssignee}
          isNewTask={!!isNewTask}
          allowMultipleAssignees
          actionItemStartDate={actionItemStartDate}
          isDescriptionReadOnly={isDescriptionReadOnly}
        />
        {enableDocuments && (
          <FluentProvider theme={webLightTheme}>
            <Divider className={styles.divider} />
            <DocumentsForm
              documents={currentDocuments}
              attachDocument={attachDocuments}
              deleteDocument={deleteDocument}
              uploadInProgress={setUploadInProgress}
            />
          </FluentProvider>
        )}
        <LoadingErrorMessage
          loading={createLoading}
          error={createError}
          actionName="creating the task"
        />
        <LoadingErrorMessage
          loading={updateLoading}
          error={updateError}
          actionName="updating the task"
        />
      </form>
      <FluentProvider theme={webLightTheme}>
        <Divider className={styles.divider} />
      </FluentProvider>

      {!isNewTask && (
        <>
          <EntityTerminationTaskHistory selectedtask={selectedtask} />
        </>
      )}
    </CoherencePanel>
  );
};

export default EntityTerminationTaskEditPanel;
